﻿import React, { useEffect, useMemo, useState } from 'react';
import Button from '../common/Button.js';
import Modal from '../common/Modal.js';
import FormInput from '../common/FormInput.js';
import InventoryDialog from './InventoryDialog.js';
import Pagination from '../common/Pagination.js';
import useAppStore from '../../store/useAppStore.js';
import {
  createInventoryItem,
  deleteInventoryItem,
  fetchInventory,
  fetchStockAlerts,
  updateInventoryItem,
  adjustInventoryStock,
  increaseInventoryStock,
  resolveStockAlert
} from '../../services/inventoryApi.js';

const Inventory = () => {
  const [stockAlerts, setStockAlerts] = useState([]);
  const [dialogItem, setDialogItem] = useState(null);
  const [showInventoryDialog, setShowInventoryDialog] = useState(false);
  const [reductionItem, setReductionItem] = useState(null);
  const [showReductionDialog, setShowReductionDialog] = useState(false);
  const [reductionAmount, setReductionAmount] = useState('');
  const [reductionNote, setReductionNote] = useState('');
  const [increaseItem, setIncreaseItem] = useState(null);
  const [showIncreaseDialog, setShowIncreaseDialog] = useState(false);
  const [increaseAmount, setIncreaseAmount] = useState('');
  const [increaseNote, setIncreaseNote] = useState('');
  const [feedback, setFeedback] = useState('');
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  const {
    inventoryItems,
    inventoryFilter,
    setInventoryFilter,
    inventoryCategories,
    addInventoryCategory,
    removeInventoryCategory,
    initializeInventory,
    addInventoryItem: addInventoryItemToStore,
    updateInventoryItem: updateInventoryItemInStore,
    removeInventoryItem
  } = useAppStore();

  const loadInventory = async () => {
    try {
      await initializeInventory(fetchInventory);
      setFeedback('');
    } catch (error) {
      setFeedback(error.message || 'Unable to load inventory data.');
    }
  };

  const loadStockAlerts = async (forceReload = false) => {
    try {
      const alertsResponse = await fetchStockAlerts();
      setStockAlerts(alertsResponse.alerts || []);
    } catch (error) {
      console.warn('Unable to load stock alerts:', error.message || error);
      setStockAlerts([]);
    }
  };

  useEffect(() => {
    loadInventory();
    loadStockAlerts();
  }, []);

  const openInventoryDialog = (item = null) => {
    setDialogItem(item);
    setShowInventoryDialog(true);
    setFeedback('');
  };

  const closeInventoryDialog = () => {
    setDialogItem(null);
    setShowInventoryDialog(false);
  };

  const openReductionDialog = (item) => {
    setReductionItem(item);
    setReductionAmount('');
    setReductionNote('');
    setShowReductionDialog(true);
    setFeedback('');
  };

  const closeReductionDialog = () => {
    setReductionItem(null);
    setReductionAmount('');
    setReductionNote('');
    setShowReductionDialog(false);
  };

  const openIncreaseDialog = (item) => {
    setIncreaseItem(item);
    setIncreaseAmount('');
    setIncreaseNote('');
    setShowIncreaseDialog(true);
    setFeedback('');
  };

  const closeIncreaseDialog = () => {
    setIncreaseItem(null);
    setIncreaseAmount('');
    setIncreaseNote('');
    setShowIncreaseDialog(false);
  };

  const handleSaveInventoryItem = async (payload) => {
    try {
      if (dialogItem) {
        const response = await updateInventoryItem(dialogItem.id, { ...payload, unit: dialogItem.unit || 'pieces' });
        const updatedItem = response.inventory || response.item || response;
        updateInventoryItemInStore(dialogItem.id, updatedItem);
        setFeedback('Inventory item updated.');
      } else {
        const response = await createInventoryItem({ ...payload, unit: 'pieces' });
        const newItem = response.inventory || response.item || response;
        addInventoryItemToStore(newItem);
        setFeedback('Inventory item created.');
      }
      loadStockAlerts();
      closeInventoryDialog();
    } catch (error) {
      setFeedback(error.message || 'Unable to save inventory item.');
    }
  };

  const deleteItem = async (id) => {
    try {
      await deleteInventoryItem(id);
      removeInventoryItem(id);
      setFeedback('Inventory item deleted.');
      loadStockAlerts();
    } catch (error) {
      setFeedback(error.message || 'Unable to delete inventory item.');
    }
  };

  const handleReduceStock = async () => {
    if (!reductionItem || !reductionAmount || Number(reductionAmount) <= 0) {
      setFeedback('Enter a valid reduction amount.');
      return;
    }
    if (Number(reductionAmount) > Number(reductionItem.stock || 0)) {
      setFeedback('Reduction amount cannot exceed current stock.');
      return;
    }
    try {
      const response = await adjustInventoryStock(reductionItem.id, Number(reductionAmount), reductionNote || '');
      const newQuantity = response.newQuantity ?? Number(reductionItem.stock) - Number(reductionAmount);
      const currentItem = inventoryItems.find((i) => i.id === reductionItem.id);
      if (currentItem) {
        updateInventoryItemInStore(reductionItem.id, {
          ...currentItem,
          stock: newQuantity
        });
      }
      setFeedback(`Stock reduced by ${reductionAmount} for ${reductionItem.name}.`);
      loadStockAlerts();
      closeReductionDialog();
    } catch (error) {
      setFeedback(error.message || 'Unable to reduce stock.');
    }
  };

  const handleIncreaseStock = async () => {
    if (!increaseItem || !increaseAmount || Number(increaseAmount) <= 0) {
      setFeedback('Enter a valid increase amount.');
      return;
    }
    try {
      const response = await increaseInventoryStock(increaseItem.id, Number(increaseAmount), increaseNote || '');
      const newQuantity = response.newQuantity ?? Number(increaseItem.stock) + Number(increaseAmount);
      const currentItem = inventoryItems.find((i) => i.id === increaseItem.id);
      if (currentItem) {
        updateInventoryItemInStore(increaseItem.id, {
          ...currentItem,
          stock: newQuantity
        });
      }
      setFeedback(`Stock increased by ${increaseAmount} for ${increaseItem.name}.`);
      loadStockAlerts();
      closeIncreaseDialog();
    } catch (error) {
      setFeedback(error.message || 'Unable to increase stock.');
    }
  };

  const handleResolveAlert = async (alertId) => {
    try {
      await resolveStockAlert(alertId);
      setFeedback('Stock alert resolved.');
      loadStockAlerts();
    } catch (error) {
      setFeedback(error.message || 'Unable to resolve stock alert.');
    }
  };

  const handleAddCategory = () => {
    if (!newCategory.trim()) {
      setFeedback('Enter a category name.');
      return;
    }
    if (inventoryCategories.some((cat) => cat.value.toLowerCase() === newCategory.toLowerCase())) {
      setFeedback('This category already exists.');
      return;
    }
    addInventoryCategory(newCategory);
    setNewCategory('');
    setFeedback('Category added successfully.');
  };

  const handleRemoveCategory = (value) => {
    const itemsUsingCategory = inventoryItems.filter((item) => item.category === value);
    if (itemsUsingCategory.length > 0) {
      setFeedback(`Cannot remove category "${value}" - it's used by ${itemsUsingCategory.length} item(s).`);
      return;
    }
    removeInventoryCategory(value);
    setFeedback('Category removed successfully.');
  };

  const metrics = useMemo(() => {
    const totalStock = inventoryItems.reduce((sum, item) => sum + Number(item.stock || 0), 0);
    const lowStockCount = inventoryItems.filter((item) => {
      const minStockValue = item.minStock != null ? Number(item.minStock) : null;
      return minStockValue !== null && Number(item.stock || 0) <= minStockValue;
    }).length;
    return { totalStock, lowStockCount, itemCount: inventoryItems.length, alertCount: stockAlerts.length };
  }, [inventoryItems, stockAlerts]);

  const filteredInventory = useMemo(() => {
    const query = inventoryFilter?.trim().toLowerCase() || '';
    return inventoryItems.filter((item) => {
      const name = item.name?.toString().toLowerCase() || '';
      const category = item.category?.toString().toLowerCase() || '';
      return name.includes(query) || category.includes(query);
    });
  }, [inventoryItems, inventoryFilter]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [inventoryFilter]);

  const paginatedInventory = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredInventory.slice(startIndex, startIndex + pageSize);
  }, [filteredInventory, currentPage, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredInventory.length / pageSize));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold">Inventory Management</h1>
          <p className="text-gray-600 mt-2">Create inventory items and manage stock levels from a clean inventory view.</p>
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-4">
          <div className="rounded-2xl bg-white p-4 shadow-sm">
            <p className="text-sm uppercase tracking-wide text-gray-500">Items</p>
            <p className="text-2xl font-semibold">{metrics.itemCount}</p>
          </div>
          <div className="rounded-2xl bg-white p-4 shadow-sm">
            <p className="text-sm uppercase tracking-wide text-gray-500">Stock items</p>
            <p className="text-2xl font-semibold">{metrics.totalStock}</p>
          </div>
          <div className="rounded-2xl bg-white p-4 shadow-sm">
            <p className="text-sm uppercase tracking-wide text-gray-500">Low-stock alerts</p>
            <p className="text-2xl font-semibold">{metrics.lowStockCount}</p>
          </div>
          <div className="rounded-2xl bg-white p-4 shadow-sm">
            <p className="text-sm uppercase tracking-wide text-gray-500">Active backend alerts</p>
            <p className="text-2xl font-semibold">{metrics.alertCount}</p>
          </div>
        </div>
      </div>

      <div className="rounded-3xl bg-white p-6 shadow-md">
        {feedback && <div className="mb-4 rounded-2xl bg-blue-50 p-3 text-sm text-blue-700">{feedback}</div>}

        <div className="mb-6">
          <input
            type="text"
            placeholder="Search by item name or category..."
            value={inventoryFilter}
            onChange={(e) => setInventoryFilter(e.target.value)}
            className="w-full rounded-2xl border border-gray-300 px-4 py-3 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
          />
        </div>

        {/* Category Management */}
        <div className="mb-6 rounded-3xl border border-gray-200 bg-gray-50 p-4">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h3 className="text-lg font-semibold">Manage Categories</h3>
              <p className="text-gray-500 text-sm">Create and remove inventory categories.</p>
            </div>
            <Button variant="primary" onClick={() => setShowCategoryDialog(!showCategoryDialog)}>
              {showCategoryDialog ? 'Hide' : 'Manage'}
            </Button>
          </div>

          {showCategoryDialog && (
            <div className="space-y-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="New category name..."
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  className="flex-1 rounded-2xl border border-gray-300 px-4 py-2 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
                />
                <Button variant="primary" onClick={handleAddCategory}>Add</Button>
              </div>

              <div className="flex flex-wrap gap-2">
                {inventoryCategories.map((cat) => (
                  <div key={cat.value} className="rounded-full bg-white border border-gray-300 px-4 py-2 flex items-center gap-2 text-sm">
                    <span>{cat.label}</span>
                    <button
                      onClick={() => handleRemoveCategory(cat.value)}
                      className="text-red-600 hover:text-red-700 font-semibold"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-6">
          {stockAlerts.length > 0 && (
            <div className="rounded-3xl border border-red-200 bg-red-50 p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-red-700">Active stock alerts</h3>
                  <p className="text-sm text-red-600">Backend-generated low stock or out of stock alerts that need review.</p>
                </div>
                <span className="rounded-full bg-red-100 px-3 py-1 text-sm font-semibold text-red-700">
                  {stockAlerts.length} active alert{stockAlerts.length === 1 ? '' : 's'}
                </span>
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {stockAlerts.slice(0, 4).map((alert) => (
                  <div key={alert.id} className="rounded-2xl bg-white p-4 shadow-sm flex justify-between items-start">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{alert.stock_item_name}</p>
                      <p className="text-sm text-gray-600">{alert.alert_type.replace('_', ' ')}</p>
                      <p className="text-sm text-gray-500 mt-2">Created {new Date(alert.created_at).toLocaleString()}</p>
                    </div>
                    <Button variant="secondary" size="small" onClick={() => handleResolveAlert(alert.id)}>
                      Resolve
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-3xl border border-gray-200 bg-gray-50 p-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold">Current inventory</h3>
                <p className="text-gray-500">Sorted by stock and low alerts.</p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <span className="rounded-full bg-red-100 px-3 py-1 text-sm font-semibold text-red-700">{metrics.lowStockCount} low-stock item(s)</span>
                <Button variant="primary" onClick={() => openInventoryDialog()}>Add Item</Button>
              </div>
            </div>
            <div className="mt-5 overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr className="bg-gray-100 text-left text-xs uppercase tracking-wider text-gray-600">
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Category</th>
                    <th className="px-4 py-3">Stock</th>
                    <th className="px-4 py-3">Min Stock</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {paginatedInventory.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-4 py-8 text-center text-sm text-gray-500">
                        No inventory items match your search. Try a different name or category.
                      </td>
                    </tr>
                  ) : (
                    paginatedInventory.map((item) => {
                      const isLowStock = item.minStock != null && Number(item.stock || 0) <= Number(item.minStock);
                      return (
                        <tr key={item.id} className={isLowStock ? 'bg-red-50' : ''}>
                          <td className="px-4 py-4 text-sm font-medium text-gray-900">{item.name}</td>
                          <td className="px-4 py-4 text-sm text-gray-600">{item.category || 'Uncategorized'}</td>
                          <td className="px-4 py-4 text-sm text-gray-600">{item.stock}</td>
                          <td className="px-4 py-4 text-sm text-gray-600">{item.minStock ?? '—'}</td>
                          <td className={`px-4 py-4 text-sm font-semibold ${isLowStock ? 'text-red-600' : 'text-green-600'}`}>
                            {isLowStock ? 'Low stock' : 'Healthy'}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-600">
                            <div className="flex gap-2 flex-wrap">
                              <Button variant="primary" size="small" onClick={() => openIncreaseDialog(item)}>Increase</Button>
                              <Button variant="secondary" size="small" onClick={() => openReductionDialog(item)}>Decrease</Button>
                              <Button variant="secondary" size="small" onClick={() => openInventoryDialog(item)}>Edit</Button>
                              <Button variant="danger" size="small" onClick={() => deleteItem(item.id)}>Delete</Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="border-t p-4 flex items-center justify-between bg-gray-50">
                  <div className="text-sm text-gray-600">
                    Showing {paginatedInventory.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} to {Math.min(currentPage * pageSize, filteredInventory.length)} of {filteredInventory.length} items
                  </div>
                  <Pagination
                    currentPage={currentPage}
                    totalItems={filteredInventory.length}
                    pageSize={pageSize}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <InventoryDialog
        isOpen={showInventoryDialog}
        onClose={closeInventoryDialog}
        onSave={handleSaveInventoryItem}
        item={dialogItem}
        categoryOptions={inventoryCategories}
        loading={false}
      />

      <Modal isOpen={showReductionDialog} onClose={closeReductionDialog} title="Decrease Stock" size="medium">
        <div className="space-y-6">
          <div className="rounded-2xl bg-gray-50 p-4">
            <p className="text-sm text-gray-700"><span className="font-semibold">Item:</span> {reductionItem?.name}</p>
            <p className="text-sm text-gray-700 mt-2"><span className="font-semibold">Current stock:</span> {reductionItem?.stock}</p>
          </div>

          <FormInput
            id="reductionAmount"
            name="amount"
            label="Reduction Amount"
            type="number"
            min="0"
            placeholder="e.g. 5"
            value={reductionAmount}
            onChange={(e) => setReductionAmount(e.target.value)}
            required
          />

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Note</label>
            <textarea
              value={reductionNote}
              onChange={(e) => setReductionNote(e.target.value)}
              className="w-full rounded-2xl border border-gray-300 px-4 py-3 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
              placeholder="Reason for reduction (e.g., consumed, spoiled, sample)"
              rows="3"
            />
          </div>

          {feedback && <div className="rounded-2xl bg-blue-50 p-3 text-sm text-blue-700">{feedback}</div>}

          <Modal.Actions>
            <Button type="button" variant="secondary" onClick={closeReductionDialog}>Cancel</Button>
            <Button type="button" variant="danger" onClick={handleReduceStock}>Decrease Stock</Button>
          </Modal.Actions>
        </div>
      </Modal>

      <Modal isOpen={showIncreaseDialog} onClose={closeIncreaseDialog} title="Increase Stock" size="medium">
        <div className="space-y-6">
          <div className="rounded-2xl bg-gray-50 p-4">
            <p className="text-sm text-gray-700"><span className="font-semibold">Item:</span> {increaseItem?.name}</p>
            <p className="text-sm text-gray-700 mt-2"><span className="font-semibold">Current stock:</span> {increaseItem?.stock}</p>
          </div>

          <FormInput
            id="increaseAmount"
            name="amount"
            label="Increase Amount"
            type="number"
            min="0"
            placeholder="e.g. 10"
            value={increaseAmount}
            onChange={(e) => setIncreaseAmount(e.target.value)}
            required
          />

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Note</label>
            <textarea
              value={increaseNote}
              onChange={(e) => setIncreaseNote(e.target.value)}
              className="w-full rounded-2xl border border-gray-300 px-4 py-3 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
              placeholder="Reason for increase (e.g., new delivery, restock, return)"
              rows="3"
            />
          </div>

          {feedback && <div className="rounded-2xl bg-blue-50 p-3 text-sm text-blue-700">{feedback}</div>}

          <Modal.Actions>
            <Button type="button" variant="secondary" onClick={closeIncreaseDialog}>Cancel</Button>
            <Button type="button" variant="primary" onClick={handleIncreaseStock}>Increase Stock</Button>
          </Modal.Actions>
        </div>
      </Modal>
    </div>
  );
};

export default Inventory;
